import { type ClassificationLevel, egressFloorAllowed, foldLevelName } from "@esi/shared";

/**
 * Inline validation of a scheme being edited (openspec change classification-editor, design D6):
 * what `classificationSchemeSchema` and the write's checks refuse, plus unique names ignoring
 * case and accents (client-side only: the API does not enforce it). Derived ids are valid by
 * construction (`deriveLevelId`).
 */

export type LevelProblem = "nameEmpty" | "nameTooLong" | "nameDuplicate" | "egressStricter";

export const LEVEL_NAME_MAX = 60;

export interface SchemeProblems {
  /** Problems per level, by index in the draft; levels without one are absent. */
  levels: Map<number, LevelProblem[]>;
  /** The scheme has no level at all. */
  empty: boolean;
  /** Every problem, levels' and the scheme's. */
  count: number;
}

type Draft = Pick<ClassificationLevel, "label" | "readersFloor" | "egressFloor">;

/**
 * `edited(index)` tells which levels the admin named in this edit (new or renamed): of two levels
 * with the same name, the edited one is flagged (both when both are); when neither is (a stored
 * scheme that already holds duplicates), every one after the first.
 */
export function schemeProblems(
  levels: readonly Draft[],
  edited: (index: number) => boolean = () => true,
): SchemeProblems {
  const problems = new Map<number, LevelProblem[]>();
  const folded = levels.map((l) => foldLevelName(l.label));
  levels.forEach((level, index) => {
    const found: LevelProblem[] = [];
    const name = level.label.trim();
    if (name === "") {
      found.push("nameEmpty");
    } else if (name.length > LEVEL_NAME_MAX) {
      found.push("nameTooLong");
    } else {
      const same = folded.flatMap((other, i) => (other === folded[index] ? [i] : []));
      const flagged = same.some(edited) ? same.filter(edited) : same.slice(1);
      if (same.length > 1 && flagged.includes(index)) {
        found.push("nameDuplicate");
      }
    }
    if (
      level.egressFloor !== undefined &&
      !egressFloorAllowed(level.readersFloor, level.egressFloor)
    ) {
      found.push("egressStricter");
    }
    if (found.length > 0) {
      problems.set(index, found);
    }
  });
  const empty = levels.length === 0;
  let count = empty ? 1 : 0;
  for (const found of problems.values()) {
    count += found.length;
  }
  return { levels: problems, empty, count };
}

/**
 * The problems of `after`, a scheme edited from `before`: a level is edited when it is new or
 * renamed (how the editor and the row's Save agree on which duplicate to flag).
 */
export function editProblems(
  before: readonly Pick<ClassificationLevel, "id" | "label">[],
  after: readonly (Draft & { id: string })[],
): SchemeProblems {
  const saved = new Map(before.map((l) => [l.id, l.label]));
  return schemeProblems(after, (index) => {
    const level = after[index];
    return level !== undefined && saved.get(level.id) !== level.label;
  });
}
